import clickButton from './click-button.png'

const content = `<p>The Observer design pattern allows an object to call other objects without knowing who they are.</p>

<p>A classic example to illustrate is a UI, where actions on elements trigger reactions on other elements. For example, consider this UI where clicking on a button has the following 3 consequences:</p>

<ul><li>changing the text of the button</li><li>changing the text of a label above the textbox</li><li>enabling typing in the textbox</li></ul>

<img src=${clickButton} alt="click-on-button" />

<p>One way to implement this is to make the button call the other elements:</p>

<pre><code class="language-ruby">class TypingButton &lt; Button
  def initialize(lock_label, input_textbox)
    @typing_locked = true
    @lock_label = lock_label
    @input_textbox = input_textbox
  end
  
  def on_click
    @typing_locked = !@typing_locked
    text = "Click here to #{@typing_locked ? 'start' : 'lock'} typing"
    lock_label.text = @typing_locked ? "You can't type yet" : "You can type now"
    input_textbox.enabled = !@typing_locked
  end
end</code></pre>

<p>With the Observer design pattern, it’s not the button that calls the label and textbox directly. Instead, the button accepts a list of subscribers (line 6), which are objects that express an interest in knowing that the button has been clicked, and it notifies those objects when it has been clicked (line 14):</p>

<pre><code class="language-ruby">class TypingButton &lt; Button
  def initialize(label, textbox)
    @typing_locked = true
  end
  
  def subscribe_to_click(subscriber)
    @subscribers ||= &#91;]
    @subscribers &lt;&lt; subscriber
  end
  
  def on_click
    @typing_locked = !@typing_locked
    text = "Click here to #{@typing_locked ? 'start' : 'lock'} typing"
    @subscribers.each { |subscriber| subscriber.on_typing_button_clicked(@typing_locked) }
  end
end</code></pre>

<p>The specific reactions of the label and the textbox, and the fact that those elements react to the button click, are coded in the label and textbox themselves. The code of the label looks likes that:</p>

<pre><code class="language-ruby">class LockLabel &lt; Label
  def initialize(typing_button)
    typing_button.subscribe_to_click(self)
  end
  
  def on_typing_button_clicked(typing_locked)
    text = typing_locked ? "You can't type yet" : "You can type now"
  end
end</code></pre>

<p>And the code of the textbox:</p>

<pre><code class="language-ruby">class InputTextbox &lt; Textbox
  def initialize(typing_button)
    typing_button.subscribe_to_click(self)
  end
  
  def on_typing_button_clicked(typing_locked)
    enabled = !typing_locked
  end
end</code></pre>

<p>Using Observer in our above example has added more code. What benefit does this added code bring?</p>

<h2 id="The-advantages-of-Observer">The advantages of Observer</h2>

<p>Without Observer, adding a new element that reacts to the button (or changing the existing reactions) implies to change the code of the button. The button knew about the other elements, which means that it was coupled to them.</p>

<p>With Observer, the code of the button no longer has to change when the logic of reactions of the other elements changes. The button no longer knows about the other elements and is no longer coupled to them.</p>

<p>Observer allows to protects the caller from the changes in the callees, and thus achieves <a href="protected-variation">Protected variation</a>.</p>

<p>Note that this doesn’t mean that all the calls in a program should be replaced by observers. Indeed, Observer didn’t remove the dependency between the button and the other elements, it just turned it around: it’s now the label and textbox that know about the button.</p>

<p>In our example it makes sense, but that doesn’t mean that in general, all the calls of the application should be replaced by observers.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>In the Observer patterns, a caller accepts subscribers and notifies them</li><li>This makes the caller agnostic of the subscribers</li><li>Observer is useful to invert a dependency</li></ul>
</div>`

export default content
